import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import hostelService from '../../services/hostelService';
import studentsService from '../../services/studentsService';
import { FormSelect } from '../../components/finance';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash, CircleNotch, ArrowLeft } from '@phosphor-icons/react';
import './hostel.css';

function HostelAttendancePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    student_id: '',
    hostel_id: '',
    attendance_date: new Date().toISOString().split('T')[0],
    check_in_time: '',
    check_out_time: '',
    status: 'checked_in',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAttendance();
  }, [attendance, searchTerm, statusFilter, filterAttendance]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [attendanceData, studentsData, hostelsData] = await Promise.all([
        hostelService.listAttendance().catch(() => []),
        studentsService.listStudents().catch(() => []),
        hostelService.listHostels().catch(() => [])
      ]).catch(() => [[], [], []]);

      const studentsList = Array.isArray(studentsData) ? studentsData : studentsData.students || studentsData.items || [];
      const hostelsList = Array.isArray(hostelsData) ? hostelsData : hostelsData.hostels || [];

      setAttendance(Array.isArray(attendanceData) ? attendanceData : attendanceData.attendance || []);
      setStudents(studentsList);
      setHostels(hostelsList);
    } catch (error) {
      console.error('Error loading attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const filterAttendance = React.useCallback(() => {
    let filtered = attendance;

    if (searchTerm) {
      filtered = filtered.filter(a => {
        const student = students.find(s => s.student_id === a.student_id);
        return student?.first_name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }

    setFilteredAttendance(filtered);
  }, [attendance, searchTerm, statusFilter, students]);

  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? `${student.student_id} - ${student.first_name} ${student.last_name}` : 'Unknown';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (!formData.student_id || !formData.hostel_id) {
        toast.error('Please select a student and hostel');
        return;
      }

      if (editingId) {
        await hostelService.updateAttendance(editingId, formData);
        toast.success('Attendance updated successfully');
      } else {
        await hostelService.createAttendance(formData);
        toast.success('Attendance recorded successfully');
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({
        student_id: '',
        hostel_id: '',
        attendance_date: new Date().toISOString().split('T')[0],
        check_in_time: '',
        check_out_time: '',
        status: 'checked_in',
        notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(editingId ? 'Failed to update attendance' : 'Failed to record attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setFormData({
      student_id: record.student_id,
      hostel_id: record.hostel_id,
      attendance_date: record.attendance_date,
      check_in_time: record.check_in_time || '',
      check_out_time: record.check_out_time || '',
      status: record.status,
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await hostelService.deleteAttendance(deleteTarget);
      toast.success('Attendance record deleted successfully');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting attendance:', error);
      toast.error('Failed to delete attendance record');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading attendance...</div></Layout>;
  }

  const checkedIn = attendance.filter(a => a.status === 'checked_in').length;
  const checkedOut = attendance.filter(a => a.status === 'checked_out').length;
  const onLeave = attendance.filter(a => a.status === 'on_leave').length;

  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Hostel Attendance</h1>
              <p>Track student check-in and check-out records</p>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <Plus size={20} />
            Record Attendance
          </button>
        </div>

        <div className="stats-bar">
          <div className="stat">
            <h3>Checked In</h3>
            <p className="stat-value">{checkedIn}</p>
          </div>
          <div className="stat">
            <h3>Checked Out</h3>
            <p className="stat-value">{checkedOut}</p>
          </div>
          <div className="stat">
            <h3>On Leave</h3>
            <p className="stat-value">{onLeave}</p>
          </div>
          <div className="stat">
            <h3>Total Records</h3>
            <p className="stat-value">{attendance.length}</p>
          </div>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="checked_in">Checked In</option>
            <option value="checked_out">Checked Out</option>
            <option value="on_leave">On Leave</option>
          </select>
        </div>

        <div className="table-container">
          {filteredAttendance.length === 0 ? (
            <div className="empty-state">
              <p>No attendance records found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Attendance Date</th>
                  <th>Check-in Time</th>
                  <th>Check-out Time</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttendance.map((record) => (
                  <tr key={record.id}>
                    <td>{getStudentName(record.student_id)}</td>
                    <td>{record.attendance_date}</td>
                    <td>{record.check_in_time || '-'}</td>
                    <td>{record.check_out_time || '-'}</td>
                    <td>
                      <span className={`badge badge-${record.status === 'checked_in' ? 'success' : record.status === 'checked_out' ? 'info' : 'warning'}`}>
                        {record.status}
                      </span>
                    </td>
                    <td>{record.notes || '-'}</td>
                    <td className="action-buttons">
                      <button
                        className="btn-icon btn-primary"
                        title="Edit"
                        onClick={() => handleEdit(record)}
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => handleDelete(record.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Attendance Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h2>{editingId ? 'Edit Attendance' : 'Record Attendance'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <FormSelect
                      label="Hostel *"
                      name="hostel_id"
                      value={formData.hostel_id}
                      onChange={(e) => setFormData({ ...formData, hostel_id: e.target.value })}
                      required
                      disabled={submitting}
                      options={hostels.map(h => ({
                        value: h.id,
                        label: h.hostel_name
                      }))}
                      placeholder="Select Hostel"
                    />
                  </div>
                  <div className="form-group">
                    <FormSelect
                      label="Student *"
                      name="student_id"
                      value={formData.student_id}
                      onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                      required
                      disabled={submitting}
                      options={students.map(s => ({
                        value: s.student_id,
                        label: `${s.student_id} - ${s.first_name} ${s.last_name}`
                      }))}
                      placeholder="Select Student"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Attendance Date *</label>
                    <input
                      type="date"
                      value={formData.attendance_date}
                      onChange={(e) => setFormData({ ...formData, attendance_date: e.target.value })}
                      required
                      disabled={submitting}
                    />
                  </div>
                  <div className="form-group">
                    <FormSelect
                      label="Status *"
                      name="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      required
                      disabled={submitting}
                      options={[
                        { value: 'checked_in', label: 'Checked In' },
                        { value: 'checked_out', label: 'Checked Out' },
                        { value: 'on_leave', label: 'On Leave' }
                      ]}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Check-in Time</label>
                    <input
                      type="time"
                      value={formData.check_in_time}
                      onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                      disabled={submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label>Check-out Time</label>
                    <input
                      type="time"
                      value={formData.check_out_time}
                      onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                      disabled={submitting}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    disabled={submitting}
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Recording...
                      </span>
                    ) : 'Record Attendance'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        <DialogModal
          isOpen={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          title="Confirm delete"
          message="Delete this attendance record? This action cannot be undone."
          type="warning"
          primaryAction={{ label: deleteLoading ? 'Deleting...' : 'Delete', onClick: confirmDelete }}
          secondaryAction={{ label: 'Cancel', onClick: () => setDeleteTarget(null) }}
          showCloseButton={!deleteLoading}
          isLoading={deleteLoading}
          disableCloseWhenLoading={true}
        />
      </div>
    </Layout>
  );
}

export default HostelAttendancePage;
